import { getCookie } from '@mx/util/get-cookie';
import { LS } from '@mx/util/local-storage';
import dayjs from 'dayjs';
import { bridge } from './bridge';
import { env } from './env';
import { Runtime, runtime } from '@/common/envRuntime';
import config from '@/config/project-info';
import { monitor } from './monitor';

// 如果url后有#，则删除#，并且删除scheme协议头
export const checkUrl = (url: string): string => {
  if (/^http/.test(url)) {
    if (url.split('#')[1] === '/') {
      url = url.split('#')[0];
    }
    return url;
  } else {
    return decodeURIComponent(url.split('url=')[1]);
  }
};

// base64解码防止出现中文乱码
export const base64StringToJSONObject = (param: string): object => {
  const jsonObj = JSON.parse(decodeURIComponent(escape(window.atob(param))));
  return jsonObj;
};

// 路由导航
export const routerPush = (that: any, name: string, data: object) => {
  that.$router.push({
    name,
    params: {
      data,
    },
  });
};

/**
 * @description: 从独立验证拿payToken，优先级 localstorage -> KNB -> cookie
 * @param {*} string
 * @return {*}
 */
export const getPayTokenFromPasswordVerify = (): Promise<string> => {
  const payTokenLS = LS.get('payParamToken');
  const payTokenCookie = getCookie('payParamToken');
  let payTokenKNB = '';

  return new Promise((resolve, reject) => {
    if (payTokenLS) {
      resolve(payTokenLS);
    } else {
      bridge
        .getStorage('payParamToken')
        .then((res) => {
          if (res.value) {
            payTokenKNB = res.value;
          }
          resolve(payTokenKNB || payTokenCookie);
        })
        .catch(() => {
          resolve(payTokenCookie);
        });
    }
  });
};

export const getStorage = (key: string) => {
  return bridge
    .getStorage(key)
    .then((res) => {
      if (!res.value) throw res;
      return res.value;
    })
    .catch(() => {
      return LS.get(key);
    });
};

export const removeStorage = (key: string) => {
  bridge.clearStorage(key);
  LS.remove(key);
};

// 错误提示增加 (错误码)，便于后端根据客诉截图定位问题
export const addErrorCode = (message: string, code: string, level = 1): string => {
  if (level !== 5 && code) {
    message += `<span class="error-code">(${code})</span>`;
  }
  return message;
};

export const decodeMeituanPaymentURL = (url: string) => {
  const prefix = 'meituanpayment://www.meituan.com/web?url=';
  if (url.startsWith(prefix)) {
    return decodeURIComponent(url.replace(prefix, ''));
  }
  return url;
};

// 滚动到目标元素
export const scrollTo = (to: number, cur: number = 0, step: number = 10, callback?: () => void) => {
  try {
    const { system, osVersion } = env;
    if (
      system &&
      ((system === 'android' && Number(osVersion) >= 10) || (system === 'iphone' && Number(osVersion) >= 13)) &&
      window.requestAnimationFrame &&
      to
    ) {
      // 系统是高版本并且 to 和 requestAnimationFrame 存在时
      const speed = Math.floor(to / step);
      if (cur > to) {
        cur = to;
      }
      const ret = window.requestAnimationFrame(() => {
        document.body.scrollTop = cur;
        document.documentElement.scrollTop = cur;
        if (cur === to) {
          window.cancelAnimationFrame(ret);
          if (callback) {
            callback();
          }
          return;
        }
        cur += speed;
        scrollTo(to, cur, step, callback);
      });
    } else if (to) {
      // 系统是低版本或者系统是高版本但 requestAnimationFrame 不存在时，直接跳转，没有平滑的滚动
      document.documentElement.scrollTop = to;
      document.body.scrollTop = to;
      if (callback) {
        callback();
      }
    } else {
      if (callback) {
        callback();
      }
    }
  } catch (e) {
    if (callback) {
      callback();
    }
  }
};

/**
 * 将十六进制颜色值转换为 RGB
 * @param {string} hexStr - 十六进制颜色值，如 "#ff5733" 或 "ff5733"
 * @param {number} opacity - 透明度
 * @returns {string} RGB 颜色值，如 "rgb(255, 87, 51)"
 */
export const hexToRgb = (hexStr: string, opacity: number = 1): string => {
  // 移除开头的 "#"（如果有的话）
  let hex = hexStr.replace(/^#/, '');

  // 确保是 6 位数的 hex 颜色
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((hex) => hex + hex)
      .join('');
  }

  // 解析 RGB 组件
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // 返回 RGBA 格式
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * @description 将对象的每个属性转为字符串
 * @param {Object} obj - 需要转换的对象，键为字符串，值可以是布尔值、数字或字符串
 * @returns {Object} 返回一个新对象，其中所有属性值都被转换为字符串
 */
export const convertObjAttrToStr = (obj: { [key: string]: boolean | number | string }): { [key: string]: string } => {
  const newObj = {} as any;
  Object.keys(obj).forEach((key) => {
    newObj[key] = `${obj[key]}`;
  });
  return newObj;
};

/**
 * @description 节流函数
 * @param {Function} func - 需要被节流的函数
 * @param {number} waitTime - 时间间隔（毫秒），在此时间内函数只会被调用一次
 * @returns {Function} 返回一个节流后的函数
 */
export const throttle = (func: (...argv: any[]) => any, waitTime: number) => {
  let previous = 0;
  return function (this: unknown, ...args: any[]) {
    const now = Date.now();
    if (now - previous > waitTime) {
      func.apply(this, args);
      previous = now;
    }
  };
};

export const initCoverage = () => {
  // 只有是测试环境才执行测试覆盖率
  if (runtime === Runtime.Test) {
    const covReport = () => import('@era/coverage-report');
    covReport()
      .then(({ initOrange }) => {
        initOrange({ projectKey: config.projectKey, commitId: config.commitId });
      })
      .catch((err) => {});
  }
};

export const appendQueryParams = (url: string, params: any) => {
  if (!params || typeof params !== 'object') {
    return url;
  }

  const urlObj = new URL(url, window.location.origin);
  const queryParams = urlObj.searchParams;

  Object.keys(params).forEach((key) => {
    queryParams.set(key, params[key]);
  });

  urlObj.search = queryParams.toString();

  if (url.includes('#')) {
    const [, hashPart] = url.split('#');
    return `${urlObj.toString().split('?')[0]}?${queryParams.toString()}#${hashPart}`;
  }

  return urlObj.toString();
};

export const formatUnixDate = (unixTimeStamp: number | string, formatStr = 'YYYY-MM-DD') => {
  return dayjs.unix(+unixTimeStamp).format(formatStr);
};

export const getUrl = (path: string) => {
  return `${window.location.origin}${runtime <= Runtime.Test ? '/resource/ibalance' : ''}${path}`;
};

/**
 * @description (区分环境)获取实名非本人自助处理页面 URL
 * @returns {String} 实名非本人自助处理页面 URL
 */
export const getBalanceDisposalUrl = () => {
  switch (runtime) {
    case Runtime.Local:
    case Runtime.Dev:
    case Runtime.Test:
      return 'https://stable.pay.test.sankuai.com/verified-sdk/balance-disposal.html?byUser=1';
    case Runtime.Staging:
      return 'https://stable.pay.st.sankuai.com/verified-sdk/balance-disposal.html?byUser=1';
    case Runtime.Prod:
      return 'https://npay.meituan.com/verified-sdk/balance-disposal.html?byUser=1';
    default:
      monitor.captureJSErrorMessage(`获取实名非本人页面 URL 触发兜底逻辑! 当前runtime: ${runtime}`);
      // 兜底进入生产环境地址
      return 'https://npay.meituan.com/verified-sdk/balance-disposal.html?byUser=1';
  }
};

export const initMiniProgramLx = () => {
  if (env.isWXMiniProgram && (window as any).LXAnalytics) {
    const userId = env?.uid;
    if (userId) {
      (window as any).LXAnalytics('set', 'uid', userId);
    }
  }
};
